// Admin Product Costs Management Page
import React, { useState } from 'react';
import { productCosts, analyzeAllProductMargins, getProductCostInfo } from '../../lib/productCosts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ProductCostBreakdown } from '../../components/ProductCostBreakdown';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, Euro, Package } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function ProductCostsPage() {
  const { isAdmin } = useAuth();
  const [sortBy, setSortBy] = useState('profit'); // profit, margin, name

  if (!isAdmin) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p>You need admin privileges to view this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analysis = analyzeAllProductMargins();
  const totalProfit = analysis.reduce((sum, item) => sum + item.profit, 0);
  const avgMargin = analysis.reduce((sum, item) => sum + parseFloat(item.margin), 0) / analysis.length;
  const needsReview = analysis.filter(item => item.needsReview).length;

  const sortedAnalysis = [...analysis].sort((a, b) => {
    switch (sortBy) {
      case 'profit':
        return b.profit - a.profit;
      case 'margin':
        return parseFloat(b.margin) - parseFloat(a.margin);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Product Cost Management
          </h1>
          <p className="text-xl text-muted-foreground">
            Analyze margins, costs, and pricing for all products
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="backdrop-blur-sm bg-card/80 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Euro className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Total Profit Potential</p>
              <p className="text-3xl font-bold text-foreground">
                €{totalProfit.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Per unit sold</p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-card/80 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Avg Profit Margin</p>
              <p className="text-3xl font-bold text-foreground">
                {avgMargin.toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Across all products</p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-card/80 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Package className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Products Tracked</p>
              <p className="text-3xl font-bold text-foreground">
                {analysis.length}
              </p>
              <p className="text-xs text-muted-foreground mt-1">With cost data</p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-card/80 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                {needsReview > 0 ? (
                  <AlertCircle className="h-8 w-8 text-primary" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-primary" />
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-1">Needs Review</p>
              <p className="text-3xl font-bold text-foreground">
                {needsReview}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Price adjustments</p>
            </CardContent>
          </Card>
        </div>

        {/* Sort Options */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-sm font-semibold text-muted-foreground">Sort by:</span>
          <div className="flex gap-2">
            <Button
              variant={sortBy === 'profit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('profit')}
            >
              Profit
            </Button>
            <Button
              variant={sortBy === 'margin' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('margin')}
            >
              Margin %
            </Button>
            <Button
              variant={sortBy === 'name' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('name')}
            >
              Name
            </Button>
          </div>
        </div>

        {/* Products Table */}
        <div className="space-y-4">
          {sortedAnalysis.map((item) => {
            const needsReview = item.needsReview;
            const costInfo = getProductCostInfo(item.productId);

            return (
              <Card
                key={item.productId}
                className={`overflow-hidden backdrop-blur-sm bg-card/70 hover:shadow-xl transition-all duration-300 ${
                  needsReview ? 'border-2 border-primary/60' : 'border-2 border-border'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold">{item.name}</h3>
                        {needsReview ? (
                          <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Review
                          </Badge>
                        ) : (
                          <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5" />
                            Good
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Cost</p>
                          <p className="text-lg font-semibold">€{costInfo.totalCost}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Current Price</p>
                          <p className="text-lg font-semibold">€{item.currentPrice.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Suggested Price</p>
                          <p className="text-lg font-semibold text-primary">
                            €{item.suggestedPrice.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Profit</p>
                          <p className="text-lg font-semibold text-primary">
                            €{item.profit.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Margin</p>
                          <p className="text-lg font-semibold text-primary">
                            {item.margin}
                          </p>
                        </div>
                      </div>

                      {/* Volume Projections */}
                      <div className="flex gap-4 text-sm">
                        <div className="bg-muted/60 px-3 py-2 rounded">
                          <span className="text-muted-foreground">10 units:</span>
                          <span className="font-semibold text-primary ml-2">
                            €{(item.profit * 10).toFixed(0)}
                          </span>
                        </div>
                        <div className="bg-muted/60 px-3 py-2 rounded">
                          <span className="text-muted-foreground">50 units:</span>
                          <span className="font-semibold text-primary ml-2">
                            €{(item.profit * 50).toFixed(0)}
                          </span>
                        </div>
                        <div className="bg-muted/60 px-3 py-2 rounded">
                          <span className="text-muted-foreground">100 units:</span>
                          <span className="font-semibold text-primary ml-2">
                            €{(item.profit * 100).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4">
                      <ProductCostBreakdown productId={item.productId} isAdmin={true} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
